import React from "react";
import { Alert, Box, IconButton, Tooltip } from "@mui/material";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import { SnippetFolderOutlined as SnippetFolderOutlinedIcon } from "@mui/icons-material";
import { ConfettiReveal, Typography } from "@platform/design-system";

import type { ExportStatus, SubmissionStatus } from "./api";

// A submission that succeeded while its export did not is two statuses, so the
// export failure gets its own warning alert under the success one.
const EXPORT_WARNINGS: Partial<Record<ExportStatus, string>> = {
  failed:
    "The excel export could not be generated. Please try exporting again from the RFP page.",
  "upload-failed":
    "The excel export was generated but could not be uploaded to SharePoint. A copy has been downloaded to your computer.",
};

interface SubmissionAlertProps {
  status: SubmissionStatus;
  error?: string | null;
  exportStatus?: ExportStatus;
  analystDocsFolderUrl?: string;
}

export function SubmissionAlert({
  status,
  error,
  exportStatus = "idle",
  analystDocsFolderUrl,
}: SubmissionAlertProps): React.JSX.Element | null {
  if (status === "submitting") {
    return (
      <Alert severity="warning" variant="outlined">
        Please wait while we are submitting
      </Alert>
    );
  }

  if (status === "exporting") {
    return (
      <Alert severity="warning" variant="outlined">
        RFP submitted successfully. Please wait while we generate the export…
      </Alert>
    );
  }

  if (status === "success") {
    const exportWarning = EXPORT_WARNINGS[exportStatus];
    return (
      <ConfettiReveal show={true}>
        <Box>
          <Alert
            severity="success"
            variant="outlined"
            icon={<CheckCircleOutlinedIcon />}
            sx={{ "& .MuiAlert-message": { width: "100%" } }}
          >
            <Box>
              <Typography variant="body-medium" color="inherit">
                RFP has been submitted successfully. Please proceed to Track to
                monitor the progress of your submission
              </Typography>
              {exportWarning ? null : (
                <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                  <Typography variant="body-medium" color="inherit">
                    An excel version of this RFP is now available in Documents
                  </Typography>
                  <Tooltip title="Documents">
                    <IconButton
                      size="small"
                      sx={{ color: "#7B1FA2", ml: 0.5 }}
                      disabled={!analystDocsFolderUrl}
                      onClick={() => {
                        if (analystDocsFolderUrl) {
                          window.open(analystDocsFolderUrl, "_blank");
                        }
                      }}
                    >
                      <SnippetFolderOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          </Alert>
          {exportWarning ? (
            <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
              {exportWarning}
            </Alert>
          ) : null}
        </Box>
      </ConfettiReveal>
    );
  }

  if (status === "error") {
    return (
      <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="body-medium" color="inherit">
            <strong>Submission Failed</strong>
          </Typography>
          <Typography variant="body-medium" color="inherit">
            {error || "An unexpected error occurred while submitting the RFP."}
          </Typography>
        </Box>
      </Alert>
    );
  }

  return null;
}
